import {gql} from '@apollo/client';
import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import * as benchlingUtil from 'BenchlingUtil';
import {BulkImportOptions, SelectedDestination} from 'bulk-import/SpreadsheetBulkImportStore';
import {
  getRegistrySupportedOligoAwareTypes,
  SchemalessBulkImportSupportedOligoAwareTypes,
} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import HelpButton from 'help/HelpButton';
import {isComplexPolymerSchema} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import NavStore from 'nav/NavStore';
import {
  TagSchemaCreatableEntityTypeFragment,
  tagSchemaToCreatableEntityType,
} from 'registry/create-bioentity/entity-creation-config/CreatableEntityTypeGetter';
import {isEntityCreationFeatureEnabled} from 'registry/create-bioentity/entity-creation-config/EntityCreationConfig';
import {EntityCreationFeatureType} from 'registry/create-bioentity/entity-creation-config/EntityCreationConfigTypes';
import * as RegistryUtil from 'registry/RegistryUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import ModalButtonKey from '../../test/lib/cypress/shared/ModalButtonKey';
import * as SpreadsheetBulkImportActions from '../SpreadsheetBulkImportActions';
import {OPTIONS, SelectedSchemaIds} from '../SpreadsheetBulkImportConstants';
import {CreatableTagSchemasQuery} from './__generated/CreatableTagSchemasQuery';
import BioentityImportOptions from './BioentityImportOptions';

export const CREATABLE_TAG_SCHEMAS_QUERY = gql`
  query CreatableTagSchemasQuery @skipAudit @engComponent(name: REGISTRY) {
    tagSchemas {
      ...TagSchemaCreatableEntityTypeFragment
    }
  }
  ${TagSchemaCreatableEntityTypeFragment.fragment}
`;

export interface ChooseImportLocationModalPaneProps {
  selectedDestination: SelectedDestination | null;
  getEnabledOptions?: ({
    options,
    selectedSchemaIds,
  }: {
    options?: BulkImportOptions;
    selectedSchemaIds: SelectedSchemaIds;
  }) => Array<OPTIONS>;
  // True if there's anything stopping us from proceeding to the next step. In practice this
  // happens when we are creating or updating samples and the batch schema hasn't been loaded yet.
  isLoading: boolean;
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  onDestinationFolderChange?: (folderId: string | null) => void;
  options: BulkImportOptions;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes | null;
  selectedSchemaIds: SelectedSchemaIds;
  showRegistries: boolean;
  showInventory: boolean;
  shouldShowSchemaSelector?: boolean;
  isSchemaRequired?: boolean;
  folderSelectorLabel?: string;
  folderSelectorPopoverContent?: string;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
  // Whether to filter the schema selector to only show complex polymer schemas
  shouldFilterToComplexPolymerSchemas: boolean;
}

export type ChooseImportLocationModalPaneConfiguration = Omit<
  ChooseImportLocationModalPaneProps,
  | 'selectedDestination'
  | 'isLoading'
  | 'modalProps'
  | 'options'
  | 'selectedSchemaIds'
  | 'schemalessCreationEntityType'
>;

class ChooseImportLocationModalPane extends React.Component<ChooseImportLocationModalPaneProps> {
  /**
   * Returns configuration for ChooseImportLocationModalPane for a given import flow.
   * @param {showRegistries}     Boolean whether to include registries in folder selector.
   * @param {showInventory}      Boolean whether to include inventory folders in folder selector.
   * @param {getEnabledOptions}  Called with ({options, selectedSchemaIds}) and should return an array of
   *                             enabled options.
   * @param {shouldShowSchemaSelector} Boolean whether to show the schema selector
   * @param {isSchemaRequired} Boolean whether or not selecting a schema should be required
   */
  static configure({
    showRegistries,
    showInventory,
    onDestinationFolderChange,
    getEnabledOptions = () => [],
    shouldShowSchemaSelector = true,
    isSchemaRequired = true,
    folderSelectorLabel,
    folderSelectorPopoverContent,
    shouldFilterToComplexPolymerSchemas = false,
  }: ChooseImportLocationModalPaneConfiguration): ChooseImportLocationModalPaneConfiguration {
    assert(showRegistries != null, 'Must configure with `showRegistries`.');
    // TODO(CUST-674): change to simple assert once we are confident there are no missed callsites
    assertAndContinue(showInventory != null, 'Must configure with `showInventory`.');
    return {
      showRegistries,
      showInventory,
      getEnabledOptions,
      shouldShowSchemaSelector,
      onDestinationFolderChange,
      isSchemaRequired,
      folderSelectorLabel,
      folderSelectorPopoverContent,
      shouldFilterToComplexPolymerSchemas,
    };
  }

  static defaultProps = {
    isSchemaRequired: true,
  };

  _handleDestinationFolderChange = (e: {target: {value: string}}): void => {
    // If the user selects a registry, we'll get a special combined value that we need to extract.
    // parseFolderValue also handles that case where it's just a regular folderId.
    const {folderId, registryId} = RegistryUtil.parseFolderValue(e.target.value);
    SpreadsheetBulkImportActions.setImportSetting({selectedDestination: {folderId, registryId}});
    if (this.props.onDestinationFolderChange != null) {
      this.props.onDestinationFolderChange(folderId);
    }
  };

  _handleTagSchemaChange = (tagSchema: {id: string} | null): void => {
    if (tagSchema != null) {
      SpreadsheetBulkImportActions.setImportSetting({selectedTagSchemaId: tagSchema.id});
    } else if (!this.props.isSchemaRequired) {
      SpreadsheetBulkImportActions.setImportSetting({selectedTagSchemaId: null});
    } else {
      assert(false, 'TagSchemaSelector returned null for the tagSchema');
    }
  };

  _openNewSchemaPage = (): void => {
    const registry = NavStore.getRegistries()[0];
    this.props.modalProps.onRequestClose();
    benchlingUtil.navigate(
      null,
      RegistryUtil.getCreateTagSchemaUrl(registry.s_owner.url_identifier, registry.id)
    );
  };

  schemaPopoverContent = (): JSX.Element => (
    <div>
      <p>
        Schemas let you define new entity types. You can edit existing schemas and add new ones in your
        registry settings.
      </p>
    </div>
  );

  override render(): JSX.Element {
    const canProceed =
      this.props.selectedDestination &&
      (!this.props.shouldShowSchemaSelector ||
        this.props.selectedSchemaIds.tagSchemaId ||
        !this.props.isSchemaRequired) &&
      (!this.props.options[OPTIONS.IMPORT_CONTAINERS] || this.props.selectedSchemaIds.containerSchemaId) &&
      !this.props.isLoading;
    let folderId;
    let registryId: string | null | undefined;

    if (this.props.selectedDestination) {
      assert(Boolean(this.props.selectedDestination.folderId), 'Selected destination is missing folderId.');
      registryId = this.props.selectedDestination.registryId;
      folderId = this.props.selectedDestination.folderId;
    }

    const folderSelectorId = registryId
      ? // @ts-expect-error We assert above instead of enforcing folder id be non-null in onDestinationFolderChange
        RegistryUtil.generateFolderValue(folderId, registryId, '')
      : folderId;
    const hasRegistrySettingsAccess =
      NavStore.getRegistries().length &&
      NavStore.getRegistries().some((registry) => RegistryUtil.hasSettingsAccess(registry));
    return (
      <Modal
        enableRedesign={false}
        buttons={[
          {
            key: 'prev',
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: SpreadsheetBulkImportActions.goToNextPane,
            disabled: !canProceed,
            dataTestAttrs: {
              component: 'ImportLocationModal',
              element: 'footerButton',
              key: ModalButtonKey.NEXT,
            },
          },
        ]}
        {...this.props.modalProps}
      >
        <Row>
          <Col xs={12}>
            <div className="form-group">
              <span>
                <FieldLabel label={this.props.folderSelectorLabel} isRequired={true} />
                {this.props.folderSelectorPopoverContent && (
                  <HelpButton
                    renderContent={() => this.props.folderSelectorPopoverContent}
                    iconClassName="bulkImportModal-helpIcon u-tiny-spacing-left"
                  />
                )}
              </span>
              <FolderSelector
                enableRedesign={true}
                onChange={this._handleDestinationFolderChange}
                showRegistries={this.props.showRegistries}
                showInventory={this.props.showInventory}
                value={folderSelectorId}
                unsetOnFetchFail={true}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {this.props.shouldShowSchemaSelector && (
              <div>
                <div className="form-group">
                  <span>
                    <FieldLabel label="Schema" isRequired={this.props.isSchemaRequired} />
                    <HelpButton
                      renderContent={this.schemaPopoverContent}
                      iconClassName="bulkImportModal-helpIcon u-tiny-spacing-left"
                    />
                  </span>
                  <BenchlingQuery<CreatableTagSchemasQuery>
                    query={CREATABLE_TAG_SCHEMAS_QUERY}
                    renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
                  >
                    {(data) => {
                      // Create mapping from schema ID to entity type for filtering
                      const schemaIdToEntityType = Object.fromEntries(
                        data.tagSchemas.map((schemaData) => [
                          schemaData.id,
                          tagSchemaToCreatableEntityType(schemaData),
                        ])
                      );

                      const tagSchemaFilterFn = (schema: {id: string}): boolean => {
                        const creatableEntityType = schemaIdToEntityType[schema.id];
                        assert(
                          creatableEntityType != null,
                          `Expected schema.id ${schema.id} to be present in schemaIdToEntityType mapping`
                        );
                        const isImportingFromSpreadsheetEnabled = isEntityCreationFeatureEnabled(
                          creatableEntityType,
                          EntityCreationFeatureType.IMPORT_FROM_SPREADSHEET
                        );

                        if (
                          this.props.complexPolymerSchemaConfigurations != null &&
                          this.props.shouldFilterToComplexPolymerSchemas
                        ) {
                          return (
                            isImportingFromSpreadsheetEnabled &&
                            isComplexPolymerSchema(this.props.complexPolymerSchemaConfigurations, schema.id)
                          );
                        }
                        return isImportingFromSpreadsheetEnabled;
                      };

                      return (
                        <TagSchemaSelector
                          oligoAwareFolderItemTypes={
                            this.props.schemalessCreationEntityType
                              ? [this.props.schemalessCreationEntityType]
                              : getRegistrySupportedOligoAwareTypes()
                          }
                          onChange={this._handleTagSchemaChange}
                          registryId={registryId}
                          requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
                          selectedTagSchemaId={this.props.selectedSchemaIds.tagSchemaId}
                          allowNone={!this.props.isSchemaRequired}
                          tagSchemaFilterFn={tagSchemaFilterFn}
                        />
                      );
                    }}
                  </BenchlingQuery>
                </div>
                {hasRegistrySettingsAccess && (
                  <div className="u-spacing-bottom">
                    Don't see the schema you're looking for?&nbsp;
                    <a onClick={this._openNewSchemaPage}>Create a new one</a>.
                  </div>
                )}
              </div>
            )}
            <BioentityImportOptions
              options={this.props.options}
              enabledOptions={this.props.getEnabledOptions!(this.props)}
              selectedSchemaIds={this.props.selectedSchemaIds}
            />
          </Col>
        </Row>
      </Modal>
    );
  }
}

export default ChooseImportLocationModalPane;
